import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { useUser } from '@clerk/react';
import { MessagingPlatform } from '@this+that/models';
import log from 'loglevel';

import { useCustomToast } from './useCustomToast';
import { useSearchParamsUpdater } from './useSearchParamsUpdater';
import { scheduleGoogleInitialSync } from '../services/google.service';
import { scheduleMicrosoftInitialSync } from '../services/microsoft.service';
import {
  OAUTH_PARAM,
  OAUTH_POLL_PARAM,
  stripOAuthQueryParams,
} from '../utils/constants/oauthQueryParams';
import { resolveEmailConnectErrorKey } from '../utils/emailConnectErrorKey';
import { clearPostOAuthFlags } from '../utils/postOAuthReturn';

const GOOGLE_INTEGRATION = 'google';
const MICROSOFT_INTEGRATION = 'outlook';
const INTEGRATION_BASE_PATH = '/integration';

const POLL_INTERVAL_MS = 10 * 1000;
const MAX_POLL_ATTEMPTS = 10;
const POLL_ENABLED = 'true';

// Survives StrictMode double-invoke and component remounts; resets only on hard reload.
const redeemedAuthorizationCodes = new Set<string>();

interface UseOAuthCalendarConnectionParams {
  onSyncComplete?: () => void;
  skip?: boolean;
}

export const useOAuthCalendarConnection = (
  params: UseOAuthCalendarConnectionParams = {},
) => {
  const { t } = useTranslation();
  const { user, isLoaded, isSignedIn } = useUser();
  const { successMessage, errorMessage } = useCustomToast();
  const location = useLocation();
  const updateSearchParams = useSearchParamsUpdater();

  const [connectingPlatform, setConnectingPlatform] =
    useState<MessagingPlatform | null>(null);
  const [hasProcessedAuthorizationCode, setHasProcessedAuthorizationCode] =
    useState(false);
  const [isSyncingIntegration, setIsSyncingIntegration] = useState(false);
  const [hasStartedPolling, setHasStartedPolling] = useState(false);

  const setIntervalX = useCallback(
    (
      callback: () => Promise<void> | void,
      delay: number,
      repetitions: number,
      finalCallback: () => void,
    ) => {
      let count = 0;
      let hasFailed = false;
      let intervalID = 0;

      const stop = () => {
        clearInterval(intervalID);
        finalCallback?.();
      };

      // By the time this fires, the backend has already persisted the
      // integration row synchronously (see runGoogleConnection). Firing the
      // first refresh immediately — rather than waiting for the first
      // window.setInterval tick — is what makes the connected-platform UI
      // (e.g. DoBoxFirstRunBanner) reflect reality as soon as the OAuth
      // redirect lands, instead of staying stale for up to `delay` ms.
      const tick = async () => {
        if (hasFailed) {
          stop();
          return;
        }
        try {
          await callback();
        } catch (error) {
          log.error('Polling callback failed, stopping polling', error);
          hasFailed = true;
          stop();
          return;
        }
        count++;
        if (count >= repetitions) {
          stop();
        }
      };

      tick();
      intervalID = window.setInterval(tick, delay);
    },
    [],
  );

  // Armed before a poll that runs for ~100s, so it must read the params as they are when
  // it finally fires — a snapshot taken here wipes the taskGroupId/teamId stamped since.
  const removePollParam = useCallback(() => {
    setConnectingPlatform(null);
    updateSearchParams((previous) => {
      const remaining = new URLSearchParams(previous);
      remaining.delete(OAUTH_POLL_PARAM);
      return remaining;
    });
  }, [updateSearchParams]);

  const startPolling = useCallback(() => {
    if (params.onSyncComplete) {
      setIntervalX(
        params.onSyncComplete,
        POLL_INTERVAL_MS,
        MAX_POLL_ATTEMPTS,
        removePollParam,
      );
    }
  }, [params.onSyncComplete, setIntervalX, removePollParam]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);

    if (
      urlParams.get(OAUTH_POLL_PARAM) === POLL_ENABLED &&
      !isSyncingIntegration &&
      !hasStartedPolling
    ) {
      setHasStartedPolling(true);
      if (params.onSyncComplete) {
        startPolling();
      } else {
        removePollParam();
      }
    }
  }, [
    location.search,
    isSyncingIntegration,
    startPolling,
    hasStartedPolling,
    params.onSyncComplete,
    removePollParam,
  ]);

  useEffect(() => {
    if (params.skip) {
      return;
    }

    if (hasProcessedAuthorizationCode) {
      return;
    }

    if (!isLoaded || !isSignedIn) {
      return;
    }

    const isIntegrationSubRoute = location.pathname.startsWith(
      INTEGRATION_BASE_PATH,
    );
    if (isIntegrationSubRoute) {
      return;
    }

    const queryParams = new URLSearchParams(location.search);
    const authorizationCode = queryParams.get(OAUTH_PARAM.CODE);
    const state = queryParams.get(OAUTH_PARAM.STATE);
    const provider =
      queryParams.get(OAUTH_PARAM.CALENDAR) ??
      queryParams.get(OAUTH_PARAM.INTEGRATION) ??
      MICROSOFT_INTEGRATION;

    if (!authorizationCode || !user?.id) {
      return;
    }

    if (redeemedAuthorizationCodes.has(authorizationCode)) {
      return;
    }
    redeemedAuthorizationCodes.add(authorizationCode);

    const cleanupQueryParams = () => {
      updateSearchParams((previous) => {
        const remaining = stripOAuthQueryParams(previous);
        remaining.set(OAUTH_POLL_PARAM, POLL_ENABLED);
        return remaining;
      });
    };

    const handleIntegrationSyncSuccess = async (provider: string) => {
      setIsSyncingIntegration(false);
      setConnectingPlatform(null);

      const messages = {
        [GOOGLE_INTEGRATION]: {
          message: t(
            'meetingAssistantPage.googleSuccessfullyConnected.message',
          ),
          title: t('meetingAssistantPage.googleSuccessfullyConnected.title'),
        },
        [MICROSOFT_INTEGRATION]: {
          message: t(
            'meetingAssistantPage.outlookSuccessfullyConnected.message',
          ),
          title: t('meetingAssistantPage.outlookSuccessfullyConnected.title'),
        },
        default: {
          message: t(
            'meetingAssistantPage.integrationSuccessfullyConnected.message',
          ),
          title: t(
            'meetingAssistantPage.integrationSuccessfullyConnected.title',
          ),
        },
      };

      const { message, title } =
        messages[provider as keyof typeof messages] || messages.default;

      successMessage(message, title);
    };

    const handleIntegrationSyncError = (syncError: unknown) => {
      setIsSyncingIntegration(false);
      setConnectingPlatform(null);
      // The card that started this connect polls for it on the Integrations page; nothing is coming.
      clearPostOAuthFlags();
      log.error('Error with integration', syncError);
      errorMessage(
        t(
          resolveEmailConnectErrorKey(
            provider,
            syncError,
            'integration.integrationError',
          ),
        ),
      );
    };

    const syncFunctions: Record<string, () => Promise<void>> = {
      [GOOGLE_INTEGRATION]: () =>
        scheduleGoogleInitialSync(authorizationCode, state).then(() =>
          handleIntegrationSyncSuccess(GOOGLE_INTEGRATION),
        ),
      [MICROSOFT_INTEGRATION]: () =>
        scheduleMicrosoftInitialSync(authorizationCode, state).then(() =>
          handleIntegrationSyncSuccess(MICROSOFT_INTEGRATION),
        ),
    };

    setHasProcessedAuthorizationCode(true);
    setIsSyncingIntegration(true);
    if (provider === MICROSOFT_INTEGRATION) {
      setConnectingPlatform(MessagingPlatform.OUTLOOK);
    }
    // Strip the single-use OAuth code from the URL before firing the sync
    // request. If the component remounts while the request is in-flight the
    // code will already be gone, so the re-run's early guard short-circuits
    // instead of redeeming the same code a second time.
    cleanupQueryParams();

    const syncIntegration = syncFunctions[provider];

    if (syncIntegration) {
      syncIntegration().catch(handleIntegrationSyncError);
    } else {
      log.error('Unknown provider:', provider);
      setIsSyncingIntegration(false);
    }
  }, [
    params.skip,
    isLoaded,
    isSignedIn,
    user?.id,
    location.search,
    location.pathname,
    hasProcessedAuthorizationCode,
    t,
    successMessage,
    errorMessage,
    updateSearchParams,
    setIsSyncingIntegration,
  ]);

  return {
    connectingPlatform,
    isSyncingIntegration,
    isProcessingOAuth: hasProcessedAuthorizationCode && isSyncingIntegration,
  };
};
